import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  ElementRef,
  Inject,
  Injector,
  input,
  Optional,
  output,
  runInInjectionContext,
  Signal,
  signal,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { IconComponent } from '@examdojo/core/icon';
import { provideFaIcons } from '@examdojo/icons';
import { faEye, faPenToSquare } from '@fortawesome/pro-light-svg-icons';
import {
  PreventBlockMergeExtension,
  TiptapEditorOutputFormat,
  TiptapTableOfContentsComponent,
} from '@examdojo/core/tiptap';
import { TiptapEditorWrapperComponent } from '@examdojo/core/tiptap-react-ui-components';
import type { UploadFunction } from '@examdojo/core/tiptap-react-ui-components/components/tiptap-node/image-upload-node/image-upload-node-extension';
import { ImageUploadNode } from '@examdojo/core/tiptap-react-ui-components/components/tiptap-node/image-upload-node/image-upload-node-extension';
import type { TiptapSmartLinkConfig } from '@examdojo/core/tiptap-react-ui-components/contexts/smart-link-context';
import { MAX_FILE_SIZE } from '@examdojo/core/tiptap-react-ui-components/lib/tiptap-utils';
import { distinctUntilDeepChanged } from '@examdojo/rxjs';
import { SMART_LINK_CONFIG, SmartLinkConfig, toReactSmartLinkConfig } from '@examdojo/smart-link';
import { EditorWithPopoverService, PopoverExtension, PopoverService } from '@examdojo/tiptap-popover';
import { isNotNullish } from '@examdojo/util/nullish';
import { TranslocoPipe } from '@jsverse/transloco';
import { JSONContent } from '@tiptap/core';
import type { Editor } from '@tiptap/react';
import { combineLatest, debounceTime, filter, map, merge, tap } from 'rxjs';
import { contentBlocksToTiptap, tiptapToContentBlocks } from '../converters';
import { ContentBlock } from '../model';
import { ContentBlocksRendererComponent } from '../renderer';
import { ContentBlockTocComponent } from '../renderer/table-of-contents';
import { AccordionExtension, AccordionPanelExtension } from './extensions/accordion';
import { ColumnExtension, ColumnsExtension } from './extensions/columns';
import { CustomComponentExtension } from './extensions/custom-component';
import { CustomIconExtension } from './extensions/custom-icon';
import { DataDisplayExtension } from './extensions/data-display';
import { FormulaBookletUrlService, FormulaCalloutExtension } from './extensions/formula-callout';
import { EchartsExtension } from './extensions/echarts';
import { GojsDiagramExtension } from './extensions/gojs-diagram';
import { KeyPointCalloutExtension } from './extensions/key-point-callout';
import { SimpleCalloutExtension } from './extensions/simple-callout';
import { SimpleSolverExtension } from './extensions/simple-solver';
import { SolverExtension } from './extensions/solver';
import { SuperLatexExtension } from './extensions/super-latex';
import { WidgetExtension } from './extensions/widget';
import { STUDY_NOTES_SLASH_MENU_ITEMS } from './slash-menu-items';

@Component({
  selector: 'dojo-study-notes-editor',
  templateUrl: './study-notes-editor.component.html',
  styleUrls: ['./study-notes-editor.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { class: 'block h-full' },
  imports: [
    TiptapEditorWrapperComponent,
    TiptapTableOfContentsComponent,
    ReactiveFormsModule,
    ContentBlocksRendererComponent,
    ContentBlockTocComponent,
    MatButtonToggleModule,
    IconComponent,
    TranslocoPipe,
  ],
  providers: [PopoverService, FormulaBookletUrlService],
})
export class StudyNotesEditorComponent {
  constructor(
    private readonly injector: Injector,
    private readonly popoverService: PopoverService,
    private readonly formulaBookletUrlService: FormulaBookletUrlService,
    @Optional() @Inject(SMART_LINK_CONFIG) private readonly smartLinkConfig: SmartLinkConfig | null,
  ) {
    provideFaIcons([faPenToSquare, faEye]);
    merge(this.syncContentToEditor(), this.syncEditorToContentChange()).pipe(takeUntilDestroyed()).subscribe();
    effect(() => this.configurePopoverService());
    effect(() => this.formulaBookletUrlService.url.set(this.formulaBookletUrl()));
  }

  readonly content = input<ContentBlock[]>([]);
  readonly editable = input<boolean>(true);
  readonly showTableOfContents = input<boolean>(false);
  readonly contentPadding = input<string>('48px 96px');
  readonly imageUploadFn = input<UploadFunction>();
  readonly formulaBookletUrl = input<string>('');
  readonly resolveImageSrcFn = input<(src: string) => string>();
  readonly toRelativeImageSrcFn = input<(src: string) => string>();

  readonly showPreviewToggle = input<boolean>(false);

  readonly contentChange = output<ContentBlock[]>();
  readonly editorReady = output<Editor>();

  readonly previewing = signal(false);
  readonly liveBlocks = signal<ContentBlock[]>([]);

  readonly editor = signal<Editor | null>(null);
  private readonly editorWrapper =
    viewChild<TiptapEditorWrapperComponent<TiptapEditorOutputFormat.Json>>('editorWrapper');

  private readonly scrollableContainerRef = viewChild<ElementRef<HTMLElement>>('scrollableContainerRef');
  readonly scrollableContainer = computed(() => this.scrollableContainerRef()?.nativeElement);
  private readonly previewContentRef = viewChild<ElementRef<HTMLElement>>('previewContentRef');
  readonly previewContent = computed(() => this.previewContentRef()?.nativeElement);

  readonly editorControl = new FormControl<JSONContent>({}, { nonNullable: true });

  readonly editorOptions: Signal<Partial<Editor['options']>> = computed(() => ({
    extensions: [
      PreventBlockMergeExtension,
      KeyPointCalloutExtension(this.injector),
      SimpleCalloutExtension(this.injector),
      PopoverExtension(this.injector),
      SuperLatexExtension(this.injector),
      SolverExtension(this.injector),
      SimpleSolverExtension(this.injector),
      WidgetExtension(this.injector),
      CustomComponentExtension(this.injector),
      DataDisplayExtension(this.injector),
      FormulaCalloutExtension(this.injector),
      GojsDiagramExtension(this.injector),
      EchartsExtension(this.injector),
      AccordionExtension(this.injector),
      AccordionPanelExtension(this.injector),
      ColumnsExtension(this.injector),
      ColumnExtension(),
      CustomIconExtension(this.injector),
      ...(this.imageUploadFn()
        ? [
            ImageUploadNode.configure({
              accept: 'image/*',
              maxSize: MAX_FILE_SIZE,
              limit: 3,
              upload: this.stableImageUpload,
              onError: (error: Error) => console.error('Upload failed:', error),
            }),
          ]
        : []),
    ],
    editable: this.editable(),
    onCreate: ({ editor }: { editor: EditorWithPopoverService }) => {
      this.editor.set(editor);
      editor.storage.popoverService = this.popoverService;
      this.editorReady.emit(editor);
    },
  }));

  readonly tableOfContentsData = computed(() => this.editorWrapper()?.tableOfContentData() ?? []);

  readonly slashMenuItems = STUDY_NOTES_SLASH_MENU_ITEMS;
  readonly TiptapEditorOutputFormat = TiptapEditorOutputFormat;

  readonly reactSmartLinkConfig = computed<TiptapSmartLinkConfig | undefined>(() => {
    const smartLinkConfig = this.smartLinkConfig;
    if (!smartLinkConfig) {
      return undefined;
    }

    return runInInjectionContext(this.injector, () => toReactSmartLinkConfig(smartLinkConfig));
  });

  /**
   * Stable upload wrapper that delegates to the current `imageUploadFn` signal at call time.
   * This avoids the stale-closure problem: the Tiptap `useEditor` hook creates the editor
   * once and does not reconfigure extensions, so a closure captured at creation time would
   * always target the originally-selected file.
   */
  private readonly stableImageUpload: UploadFunction = (file, onProgress, abortSignal) => {
    const fn = this.imageUploadFn();
    if (!fn) {
      return Promise.reject(new Error('No image upload function configured'));
    }
    return fn(file, onProgress, abortSignal);
  };

  private syncContentToEditor() {
    return combineLatest([
      toObservable(this.content).pipe(distinctUntilDeepChanged()),
      toObservable(this.editor).pipe(filter(isNotNullish)),
    ]).pipe(
      tap(([blocks]) => {
        try {
          const resolve = this.resolveImageSrcFn();
          this.editorControl.setValue(
            contentBlocksToTiptap(blocks, resolve ? { transformImageSrc: resolve } : undefined),
            { emitEvent: false },
          );
          this.liveBlocks.set(blocks);
        } catch (err) {
          console.error('Failed to convert ContentBlock[] to Tiptap:', err);
        }
      }),
    );
  }

  private syncEditorToContentChange() {
    return this.editorControl.valueChanges.pipe(
      debounceTime(300),
      map((value) => {
        const editor = this.editor();
        if (!editor) {
          return null;
        }
        try {
          const strip = this.toRelativeImageSrcFn();
          return tiptapToContentBlocks(value, strip ? { transformImageSrc: strip } : undefined);
        } catch (err) {
          console.error('Failed to convert Tiptap to ContentBlock[]:', err);
          return null;
        }
      }),
      filter(isNotNullish),
      distinctUntilDeepChanged(),
      tap((blocks) => {
        this.liveBlocks.set(blocks);
        this.contentChange.emit(blocks);
      }),
    );
  }

  private configurePopoverService(): void {
    this.popoverService.config = {
      allowEditing: this.editable(),
      editorOptions: {
        extensions: [
          PreventBlockMergeExtension,
          KeyPointCalloutExtension(this.injector),
          SimpleCalloutExtension(this.injector),
          SuperLatexExtension(this.injector),
          SolverExtension(this.injector),
        ],
      },
      slashMenuItems: this.slashMenuItems,
      onContentChange: (popoverId, jsonContent) => {
        let parsedContent;
        try {
          parsedContent = JSON.parse(jsonContent);
        } catch {
          console.error('Failed to parse popover content change:', jsonContent);
          return;
        }
        this.editor()?.commands.updatePopoverById(popoverId, { jsonContent: parsedContent });
      },
      onLinkUrlChange: (popoverId, linkUrl) => {
        this.editor()?.commands.updatePopoverById(popoverId, { linkUrl: linkUrl || undefined });
      },
    };
  }
}
